import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog'
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from '@/components/ui/collapsible'
import { ArrowUpRight, ArrowDownLeft, ChevronDown, Loader2, Wallet } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Transaction, TRANSACTION_TYPES, Membership, EntryPass, TransactionBreakdown } from '@/constants'
import { useUser } from '@/hooks/use-user'
import { apiFetch } from '@/utils'
import CardBrandIcon from '@/components/CardBrandIcon'

interface TransactionDetailDialogProps {
  transaction: Transaction | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

// ─── Helpers ────────────────────────────────────────────────

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const GUEST_TYPE_LABELS: Record<string, string> = {
  couples: 'Couples',
  single_ladies: 'Single Ladies',
  single_males: 'Single Males'
}

function formatGuestType(type?: string | null) {
  if (!type) return null
  return GUEST_TYPE_LABELS[type] ?? type
}

function formatDateRange(start: string, end: string) {
  const s = new Date(start)
  const e = new Date(end)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  return `${s.toLocaleDateString('en-US', opts)} – ${e.toLocaleDateString('en-US', opts)}`
}

function formatTimeSlot(slot?: string) {
  if (!slot) return null
  return slot === 'daytime' ? 'Daytime' : slot === 'evening' ? 'Evening' : slot
}

// ─── Detail row ─────────────────────────────────────────────

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (!value) return null
  return (
    <div className="flex justify-between items-start gap-3 py-1.5">
      <span className="text-[#BABABB] text-[12px] font-figtree shrink-0">{label}</span>
      <span className="text-[#ffffff] text-[12px] font-medium font-figtree text-right">{value}</span>
    </div>
  )
}

// ─── Status badge ───────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    active: 'bg-green-500/20 text-green-400',
    inactive: 'bg-zinc-500/20 text-zinc-400',
    expired: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-red-500/20 text-red-400',
  }
  return (
    <span className={`text-[11px] font-medium font-figtree px-2 py-0.5 rounded-full ${colors[status] ?? 'bg-white/10 text-[#ffffff]'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

// ─── Collapsible section wrapper ────────────────────────────

function RelatedSection({
  title,
  isLoading,
  children
}: {
  title: string
  isLoading: boolean
  children: React.ReactNode
}) {
  return (
    <Collapsible>
      <CollapsibleTrigger className="w-full flex items-center justify-between px-5 py-3 border-t border-[#3C3C3C] hover:bg-white/[0.03] transition-colors group">
        <span className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree">
          {title}
        </span>
        {isLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-[#BABABB]" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-[#BABABB] transition-transform group-data-[state=open]:rotate-180" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="px-5 pb-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-[#BABABB]" />
            </div>
          ) : children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

// ─── Transaction breakdown rows ─────────────────────────────

function fmtAmount(value: string | number, isSech: boolean) {
  const n = Number(value)
  return isSech
    ? `${n.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
    : `$${n.toFixed(2)}`
}

function BreakdownRows({ tx }: { tx: TransactionBreakdown }) {
  const isSech = tx.currency === 'SECH'

  return (
    <div className="space-y-2 text-sm pt-2">
      {tx.subtotal && (
        <div className="flex justify-between">
          <span className="text-[#BABABB]">Subtotal</span>
          <span className="text-[#ffffff]">{fmtAmount(tx.subtotal, isSech)}</span>
        </div>
      )}
      {tx.discountAmount && (
        <div className="flex justify-between text-green-400">
          <span>Discount</span>
          <span>-{fmtAmount(tx.discountAmount, isSech)}</span>
        </div>
      )}
      <div className="border-t border-dashed border-[#4C4C4C] my-2" />
      {tx.taxBreakdown && tx.taxBreakdown.length > 0 && (
        <div className="flex justify-between">
          <span className="text-[#BABABB]">Taxes & Fees</span>
          <span className="text-[#ffffff]">
            {isSech
              ? `${tx.taxBreakdown.reduce((sum, t) => sum + t.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
              : `$${tx.taxBreakdown.reduce((sum, t) => sum + t.amountUSD, 0).toFixed(2)}`}
          </span>
        </div>
      )}
      <div className="flex justify-between font-semibold">
        <span className="text-[#ffffff]">Total</span>
        <span className="text-[#ffffff]">{fmtAmount(
          isSech ? (tx.totalSech ?? '0') : (tx.totalUsd ?? '0'),
          isSech
        )}</span>
      </div>
    </div>
  )
}

// ─── Membership details ─────────────────────────────────────

function MembershipDetails({ transactionId, walletAddress }: { transactionId: number; walletAddress: string }) {
  const [membership, setMembership] = useState<Membership | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetch() {
      try {
        setIsLoading(true)
        const res = await apiFetch('/v1/memberships/my', { withCredentials: true })
        if (!res.ok) throw new Error('Failed to fetch memberships')
        const data: Membership[] = await res.json()
        const match = data.find((m) => m.transactionId === transactionId)
        if (!cancelled) setMembership(match ?? null)
      } catch {
        if (!cancelled) setMembership(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetch()
    return () => { cancelled = true }
  }, [transactionId])

  return (
    <RelatedSection title="Membership Details" isLoading={isLoading}>
      {membership ? (
        <div className="divide-y divide-[#3C3C3C]/50">
          <DetailRow label="Status" value={<StatusBadge status={membership.status} />} />
          {membership.templateName && (
            <DetailRow label="Plan" value={membership.templateName} />
          )}
          {membership.guestType && (
            <DetailRow label="Guest Type" value={formatGuestType(membership.guestType)} />
          )}
          {membership.durationMonths && (
            <DetailRow label="Duration" value={`${membership.durationMonths} month${membership.durationMonths > 1 ? 's' : ''}`} />
          )}
          <DetailRow label="Period" value={formatDateRange(membership.startDate, membership.endDate)} />
          {membership.transaction ? (
            <BreakdownRows tx={membership.transaction} />
          ) : membership.price ? (
            <DetailRow label="Price" value={`$${Number(membership.price).toFixed(2)}`} />
          ) : null}
          {membership.primaryName && (
            <DetailRow label="Primary" value={membership.primaryName} />
          )}
          {membership.partnerName && (
            <DetailRow label="Partner" value={membership.partnerName} />
          )}
        </div>
      ) : (
        <p className="text-[#BABABB] text-[12px] font-figtree py-1">No membership data found for this transaction.</p>
      )}
    </RelatedSection>
  )
}

// ─── Entry pass details ─────────────────────────────────────

function EntryPassDetails({ transactionId, walletAddress }: { transactionId: number; walletAddress: string }) {
  const [entryPass, setEntryPass] = useState<EntryPass | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetch() {
      try {
        setIsLoading(true)
        const res = await apiFetch(`/v1/entry-passes/${walletAddress}/history`, { withCredentials: true })
        if (res.status === 404) {
          if (!cancelled) setEntryPass(null)
          return
        }
        if (!res.ok) throw new Error('Failed to fetch entry passes')
        const data: EntryPass[] = await res.json()
        const match = data.find((p) => p.transactionId === transactionId)
        if (!cancelled) setEntryPass(match ?? null)
      } catch {
        if (!cancelled) setEntryPass(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetch()
    return () => { cancelled = true }
  }, [transactionId, walletAddress])

  return (
    <RelatedSection title="Entry Pass Details" isLoading={isLoading}>
      {entryPass ? (
        <div className="divide-y divide-[#3C3C3C]/50">
          <DetailRow label="Status" value={<StatusBadge status={entryPass.status} />} />
          {entryPass.templateName && (
            <DetailRow label="Pass" value={entryPass.templateName} />
          )}
          {entryPass.guestType && (
            <DetailRow label="Guest Type" value={formatGuestType(entryPass.guestType)} />
          )}
          {entryPass.dayOfWeek !== undefined && entryPass.dayOfWeek !== null && (
            <DetailRow label="Day" value={DAYS_OF_WEEK[entryPass.dayOfWeek] ?? `Day ${entryPass.dayOfWeek}`} />
          )}
          {entryPass.timeSlot && (
            <DetailRow label="Time Slot" value={formatTimeSlot(entryPass.timeSlot)} />
          )}
          {entryPass.startTime && entryPass.endTime && (
            <DetailRow label="Hours" value={`${entryPass.startTime} – ${entryPass.endTime}`} />
          )}
          {entryPass.transaction ? (
            <BreakdownRows tx={entryPass.transaction} />
          ) : entryPass.price ? (
            <DetailRow label="Price" value={`$${Number(entryPass.price).toFixed(2)}`} />
          ) : null}
          {entryPass.validFor && (
            <DetailRow label="Valid Until" value={new Date(entryPass.validFor).toLocaleDateString('en-US', {
              weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
              hour: '2-digit', minute: '2-digit'
            })} />
          )}
        </div>
      ) : (
        <p className="text-[#BABABB] text-[12px] font-figtree py-1">No entry pass data found for this transaction.</p>
      )}
    </RelatedSection>
  )
}

// ─── Order details ──────────────────────────────────────────

interface OrderData {
  id: number
  orderNumber: string
  status: string
  totalUsd: string
  totalSech: string
  subtotal?: string | null
  subtotalSech?: string | null
  taxAmount?: string | null
  taxBreakdown?: Array<{
    rate: number
    type: string
    amount: number
    amountUSD: number
    description: string
  }> | null
  discountAmount?: string | null
  discountAmountSech?: string | null
  discountDetails?: {
    discountName?: string
    discountType?: string
    discountValue?: number
    appliedAmount?: number
  } | null
  sechExchangeRate?: string | null
  paymentMethod?: 'CARD' | 'TOKEN' | null
  transactionHash?: string | null
  walletAddress?: string | null
  cardBrand?: string | null
  cardLast4?: string | null
  createdAt: string
  paymentHash?: string | null
  notes?: string | null
  items?: Array<{
    id: number
    quantity: number
    unitPriceUsd: string
    unitPriceSech: string
    product?: { name: string } | null
    customDrink?: { name: string } | null
  }>
}

function OrderDetails({ orderId, isCard }: { orderId: string; isCard: boolean }) {
  const [order, setOrder] = useState<OrderData | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function fetch() {
      try {
        setIsLoading(true)
        const res = await apiFetch(`/v1/pos/orders/${orderId}`, { withCredentials: true })
        if (res.status === 404) {
          if (!cancelled) setOrder(null)
          return
        }
        if (!res.ok) throw new Error('Failed to fetch order')
        const data: OrderData = await res.json()
        if (!cancelled) setOrder(data)
      } catch {
        if (!cancelled) setOrder(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetch()
    return () => { cancelled = true }
  }, [orderId])

  return (
    <RelatedSection title="Order Details" isLoading={isLoading}>
      {order ? (
        <div className="divide-y divide-[#3C3C3C]/50">
          <DetailRow label="Order #" value={order.orderNumber} />
          <DetailRow label="Status" value={<StatusBadge status={order.status} />} />
          {order.items && order.items.length > 0 && (
            <div className="pt-2 pb-1">
              <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1.5">Items</p>
              <div className="flex flex-col gap-1">
                {order.items.map((item) => (
                  <div key={item.id} className="flex justify-between items-center">
                    <span className="text-[#ffffff] text-[12px] font-figtree">
                      {item.product?.name ?? item.customDrink?.name ?? 'Unknown Item'} &times; {item.quantity}
                    </span>
                    <span className="text-[#BABABB] text-[12px] font-figtree">
                      {isCard
                        ? `$${(Number(item.unitPriceUsd) * item.quantity).toFixed(2)}`
                        : `${(Number(item.unitPriceSech) * item.quantity).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2 text-sm pt-2">
            {order.subtotal && (
              <div className="flex justify-between">
                <span className="text-[#BABABB]">Subtotal</span>
                <span className="text-[#ffffff]">{isCard
                  ? `$${Number(order.subtotal).toFixed(2)}`
                  : order.subtotalSech
                    ? `${Number(order.subtotalSech).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                    : undefined
                }</span>
              </div>
            )}
            {order.discountDetails && order.discountAmount && (
              <div className="flex justify-between text-green-400">
                <span>Discount</span>
                <span>{isCard
                  ? `-$${Number(order.discountAmount).toFixed(2)}`
                  : order.discountAmountSech
                    ? `-${Number(order.discountAmountSech).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                    : undefined
                }</span>
              </div>
            )}
            <div className="border-t border-dashed border-[#4C4C4C] my-2" />
            {order.taxBreakdown && order.taxBreakdown.length > 0 && (
              <div className="flex justify-between">
                <span className="text-[#BABABB]">Taxes & Fees</span>
                <span className="text-[#ffffff]">{isCard
                  ? `$${order.taxBreakdown.reduce((sum, t) => sum + t.amountUSD, 0).toFixed(2)}`
                  : `${order.taxBreakdown.reduce((sum, t) => sum + t.amount, 0).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
                }</span>
              </div>
            )}
            <div className="flex justify-between font-semibold">
              <span className="text-[#ffffff]">Total</span>
              <span className="text-[#ffffff]">{isCard
                ? `$${Number(order.totalUsd).toFixed(2)}`
                : `${Number(order.totalSech).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })} SECH`
              }</span>
            </div>
          </div>
          {order.notes && (
            <DetailRow label="Notes" value={order.notes} />
          )}
        </div>
      ) : (
        <p className="text-[#BABABB] text-[12px] font-figtree py-1">No order data found for this transaction.</p>
      )}
    </RelatedSection>
  )
}

// ─── Main dialog ────────────────────────────────────────────

export default function TransactionDetailDialog({
  transaction,
  open,
  onOpenChange
}: TransactionDetailDialogProps) {
  const { walletAddress } = useUser()

  if (!transaction) return null

  const isCard = transaction.paymentMethod === 'CARD'
  const isSent = isCard || walletAddress?.toLowerCase() === transaction.fromAddress?.toLowerCase()
  const typeLabel = TRANSACTION_TYPES[transaction.type] || transaction.type
  const date = new Date(transaction.timestamp)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[420px] max-h-[85vh] overflow-y-auto bg-[#212124] border-[#4C4C4C] rounded-[16px] p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
              isSent ? 'bg-red-500/15' : 'bg-green-500/15'
            }`}>
              {isSent ? (
                <ArrowUpRight className="h-5 w-5 text-red-400" />
              ) : (
                <ArrowDownLeft className="h-5 w-5 text-green-400" />
              )}
            </div>
            <div className="text-left">
              <DialogTitle className="text-[#ffffff] text-[16px] font-semibold font-figtree">
                {isSent ? 'Sent' : 'Received'} &middot; {typeLabel}
              </DialogTitle>
              <DialogDescription className="text-[#BABABB] text-[12px] font-figtree mt-0.5">
                Transaction #{transaction.id}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Amount */}
        <div className="px-5 py-4 border-t border-[#3C3C3C]">
          <div className="text-center">
            <span className={`text-[28px] leading-[32px] font-bold font-figtree ${
              isSent ? 'text-red-400' : 'text-green-400'
            }`}>
              {isCard
                ? `$${parseFloat(transaction.amount.toString()).toFixed(2)}`
                : `${isSent ? '-' : '+'}${parseFloat(transaction.amount.toString()).toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 })}`}
            </span>
            <span className="text-[#BABABB] text-[14px] font-medium font-figtree ml-2">
              {isCard ? '' : (transaction.currency ?? 'SECH')}
            </span>
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-4 border-t border-[#3C3C3C] flex flex-col gap-4">
          {/* Date & Time */}
          <div>
            <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1">
              Date & Time
            </p>
            <p className="text-[#ffffff] text-[13px] font-medium font-figtree">
              {date.toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
              {' at '}
              {date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>

          {/* Type */}
          <div>
            <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1">
              Type
            </p>
            <span className="inline-block text-[12px] font-medium font-figtree px-2.5 py-0.5 rounded-full bg-white/10 text-[#ffffff]">
              {typeLabel}
            </span>
          </div>

          {/* Payment Method */}
          {transaction.paymentMethod && (
            <div>
              <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1">
                Payment Method
              </p>
              {isCard ? (
                transaction.paymentDetails?.last4 ? (
                  <span className="inline-flex items-center gap-2">
                    <CardBrandIcon brand={transaction.paymentDetails?.brand} width={36} />
                    <span className="text-[#ffffff] text-[13px] font-medium font-figtree">
                      ••••{transaction.paymentDetails.last4}
                    </span>
                  </span>
                ) : (
                  <span className="inline-block text-[12px] font-medium font-figtree px-2.5 py-0.5 rounded-full bg-white/10 text-[#ffffff]">
                    Card
                  </span>
                )
              ) : (
                <span className="inline-flex items-center gap-2">
                  <Wallet className="h-4 w-4 text-orange-400 shrink-0" />
                  <span className="text-[#ffffff] text-[13px] font-medium font-figtree">
                    {transaction.fromAddress
                      ? `${transaction.fromAddress.slice(0, 6)}...${transaction.fromAddress.slice(-4)}`
                      : 'Token'}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Status */}
          {transaction.status && (
            <div>
              <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1">
                Status
              </p>
              <span className={`inline-block text-[12px] font-medium font-figtree px-2.5 py-0.5 rounded-full ${
                transaction.status === 'approved' || transaction.status === 'completed'
                  ? 'bg-green-500/15 text-green-400'
                  : transaction.status === 'declined' || transaction.status === 'cancelled'
                  ? 'bg-red-500/15 text-red-400'
                  : 'bg-white/10 text-[#ffffff]'
              }`}>
                {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
              </span>
            </div>
          )}

          {/* Order ID */}
          {transaction.orderId && (
            <div>
              <p className="text-[#BABABB] text-[11px] uppercase tracking-wider font-figtree mb-1">
                Order ID
              </p>
              <p className="text-[#ffffff] text-[13px] font-medium font-figtree">
                {transaction.orderId}
              </p>
            </div>
          )}
        </div>

        {/* Related data — collapsible, starts collapsed */}
        {transaction.type === 'membership' && walletAddress && (
          <MembershipDetails transactionId={transaction.id} walletAddress={walletAddress} />
        )}
        {transaction.type === 'entry_pass' && walletAddress && (
          <EntryPassDetails transactionId={transaction.id} walletAddress={walletAddress} />
        )}
        {transaction.type === 'order' && transaction.orderId && (
          <OrderDetails orderId={transaction.orderId} isCard={isCard} />
        )}


      </DialogContent>
    </Dialog>
  )
}
